<template>
  <div style="min-height: 2em">
    <LoadingSpinner v-if="$apollo.loading" />
    <EmptyMessage v-if="allArticles?.length === 0">
      No study notes found for the current passage.
    </EmptyMessage>
    <ArticleDetail
      v-else-if="selectedArticleUri"
      :key="selectedArticleUri"
      :articleUri="selectedArticleUri"
      :hasMore="allArticles?.length > 1"
      @select-article-uri="uri => (selectedArticleUri = uri)"
    />
    <ArticleList
      v-else-if="articlesByCollection"
      :collections="articlesByCollection"
      @select-article-uri="uri => (selectedArticleUri = uri)"
    />
  </div>
</template>
<script>
  import mixins from '@/mixins';
  import gql from 'graphql-tag';
  import { DEFAULT_GROUP_KEY } from '@/store/constants';
  import { compareByProperty } from '@/common/sortUtils';
  import LoadingSpinner from '@/symphony/widgets/components/LoadingSpinner.vue';
  import EmptyMessage from '@/symphony/widgets/components/EmptyMessage.vue';
  import ArticleList from '@/symphony/widgets/articles-widget/ArticleList.vue';
  import ArticleDetail from '@/symphony/widgets/articles-widget/ArticleDetail.vue';

  export default {
    name: 'BiblicaStudyBibleArticlesWidget',
    mixins: [mixins.InputDataMixin, mixins.RefAdapterMixin, mixins.LocalizationMixin],
    components: {
      LoadingSpinner,
      EmptyMessage,
      ArticleDetail,
      ArticleList,
    },
    props: {
      doric: {
        inputs: {
          label: 'Biblica Study Bible Notes',
          osisRef: {
            value: 'osisRef',
            groupKey: DEFAULT_GROUP_KEY,
          },
          selectedTokenIds: {
            groupKey: DEFAULT_GROUP_KEY,
            value: 'selectedTokenIds',
          },
        },
      },
    },
    data() {
      return {
        selectedArticleUri: null,
        passageRef: null,
      };
    },
    watch: {
      usfmRef: {
        handler(newValue) {
          if (newValue) {
            this.passageRef = newValue;
          }
        },
        immediate: true,
      },
      verses(newValue) {
        if (newValue?.length) {
          this.passageRef = newValue[0].usfmRef;
        }
      },
    },
    computed: {
      allArticles() {
        if (!this.articles) {
          return [];
        }
        return (this.bookIntros || []).concat(this.studyNotes || []).concat(this.keyTerms || []);
      },
      bookIntros() {
        return this.localizedArticles(this.articles?.bookIntros);
      },
      studyNotes() {
        return this.localizedArticles(this.articles?.studyNotes).sort((a, b) =>
          a.id.localeCompare(b.id),
        );
      },
      keyTerms() {
        return this.localizedArticles(this.articles?.keyTerms).sort(
          compareByProperty('fallbackTitle'),
        );
      },
      articlesByCollection() {
        if (!this.articles) {
          return null;
        }
        const collections = [];

        if (this.bookIntros.length > 0) {
          const label =
            this.getSanitizedLabel(
              this.getLocalizedOrFallback(this.bookIntros[0]?.collection, 'label'),
            ) || 'Book Introductions';
          collections.push([label, this.bookIntros]);
        }

        if (this.studyNotes.length > 0) {
          const label =
            this.getSanitizedLabel(
              this.getLocalizedOrFallback(this.studyNotes[0]?.collection, 'label'),
            ) || 'Study Notes';
          collections.push([label, this.studyNotes]);
        }

        if (this.keyTerms.length > 0) {
          const keyTermsLabel =
            this.getSanitizedLabel(
              this.getLocalizedOrFallback(this.keyTerms[0]?.collection, 'label'),
            ) || 'Key Terms';
          const keyTermsWithPassageLabel = `${keyTermsLabel} :: ${this.passageRef}`;
          collections.push([keyTermsWithPassageLabel, this.keyTerms]);
        }

        return collections;
      },
    },
    methods: {
      getSanitizedLabel(label) {
        if (!label) {
          return null;
        }
        return label.split('(')[0].trim();
      },
      localizedArticles(articles) {
        if (!articles) {
          return [];
        }
        return articles.map(article => {
          const { uri, id, collection, title } = article;
          const fallbackTitle = this.getLocalizedOrFallback(article, 'title');
          const description = this.getLocalizedOrFallback(article, 'description');
          return {
            id,
            uri,
            title,
            fallbackTitle,
            description,
            collection,
          };
        });
      },
    },
    apollo: {
      articles: {
        query: gql`
          fragment articleFields on Article {
            id
            uri
            title(language: $language, allowFallback: false)
            description(language: $language)
            localizations {
              language
              title
              description
            }
            collection {
              id
              uri
              label
            }
          }
          query BiblicaStudyNotesArticles(
            $introUsfmRef: String!
            $contextualizedUsfmRef: String!
            $passageRef: String!
            $language: String = "eng"
          ) {
            bookIntros: articles(
              filters: {
                scriptureReference: { usfmRef: $introUsfmRef, scope: VERSE }
                collection: { AND: { uri: { inList: ["BiblicaStudyNotesBookIntros"] } } }
              }
              pagination: { limit: 100, offset: 0 }
            ) {
              ...articleFields
            }
            studyNotes: articles(
              filters: {
                scriptureReference: { usfmRef: $contextualizedUsfmRef, scope: VERSE }
                collection: { AND: { uri: { inList: ["BiblicaStudyNotes"] } } }
              }
              pagination: { limit: 100, offset: 0 }
            ) {
              ...articleFields
            }
            keyTerms: articles(
              filters: {
                scriptureReference: { usfmRef: $passageRef, scope: VERSE }
                collection: { AND: { uri: { inList: ["bib-bd"] } } }
              }
              pagination: { limit: 100, offset: 0 }
            ) {
              ...articleFields
            }
          }
        `,
        variables() {
          return {
            introUsfmRef: `${this.contextualizedUsfmRef?.split(' ')[0]} 1:1`,
            contextualizedUsfmRef: this.contextualizedUsfmRef,
            passageRef: this.passageRef,
            language: this.lang,
          };
        },
        skip() {
          return !this.osisRef;
        },
        update(data) {
          return {
            bookIntros: data.bookIntros,
            studyNotes: data.studyNotes,
            keyTerms: data.keyTerms,
          };
        },
      },
      verses: {
        query: gql`
          query WordTokenVerse($filters: WordTokenFilter) {
            wordTokens(filters: $filters) {
              id
              verse {
                usfmRef
              }
            }
          }
        `,
        variables() {
          try {
            const selectedTokenIds = JSON.parse(this.selectedTokenIds);
            return {
              filters: { id: { inList: selectedTokenIds } },
            };
          } catch (e) {
            return {
              filters: { id: { inList: [] } },
            };
          }
        },
        skip() {
          try {
            const selectedTokenIds = JSON.parse(this.selectedTokenIds);
            return !Array.isArray(selectedTokenIds) || selectedTokenIds.length === 0;
          } catch (e) {
            return true;
          }
        },
        update(data) {
          return data.wordTokens.map(token => token.verse);
        },
      },
    },
  };
</script>
